import { useState, useEffect, useMemo } from 'react';
import {
  Calculator,
  TrendingUp,
  DollarSign,
  Package,
  Loader2,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import { financeApi, storeApi } from '../lib/api';

interface StoreOption {
  id: string;
  name: string;
}

interface CalculatorProduct {
  id: string;
  productName: string;
  sellingPrice: number;
  purchasePrice: number;
  storeId?: string | null;
}

interface FormData {
  price: number;
  cost: number;
  shipping: number;
  feePercent: number;
  feeFixed: number;
  other: number;
}

interface RoasRow {
  roas: number;
  adSpend: number;
  profit: number;
  status: 'loss' | 'breakeven' | 'profit';
}

interface CalculationResult {
  vatAmount: number;
  revenueNet: number;
  fees: number;
  totalCosts: number;
  margin: number;
  marginPercent: number;
  minROAS: number;
  roasSimulation: RoasRow[];
}

// Generate dynamic ROAS simulation points based on break-even:
// 1 loss position, break-even, 5 at +0.1 each, 5 at +0.5 each
function generateRoasLevels(breakEvenRoas: number): number[] {
  const be = Math.round(breakEvenRoas * 10) / 10; // round to 1 decimal
  const levels: number[] = [];
  // 1 loss position (0.1 below break-even, min 0.1)
  levels.push(Math.max(0.1, Math.round((be - 0.1) * 10) / 10));
  // Break-even point
  levels.push(be);
  // 5 positions at +0.1 increments
  for (let i = 1; i <= 5; i++) {
    levels.push(Math.round((be + i * 0.1) * 10) / 10);
  }
  // 5 positions at +0.5 increments (starting after the last +0.1 step)
  const lastSmall = be + 0.5;
  for (let i = 1; i <= 5; i++) {
    levels.push(Math.round((lastSmall + i * 0.5) * 10) / 10);
  }
  return levels;
}

function calculate(form: FormData, isVat: boolean, vatRate: number): CalculationResult {
  const { price, cost, shipping, feePercent, feeFixed, other } = form;

  // VAT only applies if user is a VAT payer
  const vatAmount = isVat ? price * vatRate / (100 + vatRate) : 0;
  const revenueNet = price - vatAmount;
  const fees = (price * feePercent / 100) + feeFixed;
  const totalCosts = cost + shipping + fees + other;
  const margin = revenueNet - totalCosts;
  const marginPercent = price > 0 ? (margin / price) * 100 : 0;
  const minROAS = margin > 0 ? price / margin : 999;

  const roasLevels = generateRoasLevels(minROAS);
  const roasSimulation: RoasRow[] = roasLevels.map((roas) => {
    const adSpend = price / roas;
    const profit = margin - adSpend;
    const status: RoasRow['status'] =
      profit < -0.5 ? 'loss' : profit < 0.5 ? 'breakeven' : 'profit';
    return { roas, adSpend, profit, status };
  });

  return {
    vatAmount,
    revenueNet,
    fees,
    totalCosts,
    margin,
    marginPercent,
    minROAS,
    roasSimulation,
  };
}

function formatCurrency(value: number): string {
  return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

export function FinanceCalculatorPage() {
  const [allProducts, setAllProducts] = useState<CalculatorProduct[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [manualMode, setManualMode] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState('');
  const [isVat, setIsVat] = useState(true);
  const [vatRate, setVatRate] = useState(23);

  // Store selector
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('all');

  const [form, setForm] = useState<FormData>({
    price: 0,
    cost: 0,
    shipping: 0,
    feePercent: 0,
    feeFixed: 0,
    other: 0,
  });

  // Filter products by selected store
  const products = useMemo(() => {
    if (selectedStoreId === 'all') return allProducts;
    return allProducts.filter((p) => p.storeId === selectedStoreId);
  }, [allProducts, selectedStoreId]);

  // Load products, stores, and finance config from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingProducts(true);
        const [productsRes, configRes, storesRes] = await Promise.all([
          financeApi.getCalculatorProducts(),
          financeApi.getConfig(),
          storeApi.getAll(),
        ]);
        const data = productsRes.data as CalculatorProduct[] | { products: CalculatorProduct[] };
        const productList = Array.isArray(data) ? data : (data as { products: CalculatorProduct[] }).products || [];
        setAllProducts(productList);
        const config = configRes.data as { vatEnabled?: boolean; vatRate?: number };
        if (config.vatEnabled !== undefined) setIsVat(config.vatEnabled);
        if (config.vatRate !== undefined) setVatRate(config.vatRate);
        const storeList = ((storesRes.data ?? storesRes) as StoreOption[]) || [];
        setStores(storeList);
        // If only one store, auto-select it
        if (storeList.length === 1) {
          setSelectedStoreId(storeList[0].id);
        }
      } catch (err) {
        console.error('Failed to load calculator data:', err);
        setError('Nie udało się załadować danych');
      } finally {
        setLoadingProducts(false);
      }
    };
    loadData();
  }, []);

  // When a product is selected, pre-fill price and cost
  useEffect(() => {
    if (!selectedProductId || manualMode) return;
    const product = products.find((p) => p.id === selectedProductId);
    if (product) {
      setForm((prev) => ({
        ...prev,
        price: product.sellingPrice,
        cost: product.purchasePrice,
      }));
    }
  }, [selectedProductId, products, manualMode]);

  // Calculate results on the fly (using actual VAT config)
  const results = useMemo(() => calculate(form, isVat, vatRate), [form, isVat, vatRate]);

  const hasInput = form.price > 0;

  const updateField = (field: keyof FormData, value: string) => {
    const num = parseFloat(value);
    setForm((prev) => ({ ...prev, [field]: isNaN(num) ? 0 : num }));
  };

  const handleProductSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '__manual__') {
      setManualMode(true);
      setSelectedProductId('');
      setForm({ price: 0, cost: 0, shipping: 0, feePercent: 0, feeFixed: 0, other: 0 });
    } else {
      setManualMode(false);
      setSelectedProductId(value);
    }
  };

  const statusLabel = (status: RoasRow['status']): string => {
    switch (status) {
      case 'loss': return 'Strata';
      case 'breakeven': return 'Break Even';
      case 'profit': return 'Zysk';
    }
  };

  const statusColor = (status: RoasRow['status']): string => {
    switch (status) {
      case 'loss': return 'text-danger';
      case 'breakeven': return 'text-warning';
      case 'profit': return 'text-success';
    }
  };

  const statusBg = (status: RoasRow['status']): string => {
    switch (status) {
      case 'loss': return 'bg-danger/10';
      case 'breakeven': return 'bg-warning/10';
      case 'profit': return 'bg-success/10';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-xl bg-gradient-to-br from-accent-primary to-accent-info shadow-accent-primary/20/20 shadow-lg">
            <Calculator className="w-6 h-6 text-text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-text-primary">
              Kalkulator <span className="text-gradient">rentowności</span>
            </h1>
            <p className="text-text-secondary mt-1">
              Oblicz BEP (Break Even Point) i minimalny ROAS dla produktu
            </p>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger animate-fade-in">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Store + Product Selector */}
      <div className="card animate-slide-up">
        {/* Store selector badges */}
        {stores.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-accent-primary" />
              <h2 className="text-lg font-semibold text-text-primary">Wybierz sklep i produkt</h2>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {stores.length > 1 && (
                <button
                  onClick={() => {
                    setSelectedStoreId('all');
                    setSelectedProductId('');
                    setManualMode(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    selectedStoreId === 'all'
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                  }`}
                >
                  Wszystkie sklepy
                </button>
              )}
              {stores.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSelectedStoreId(s.id);
                    setSelectedProductId('');
                    setManualMode(false);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    selectedStoreId === s.id
                      ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                      : 'bg-surface-hover/50 text-text-secondary border border-border-subtle hover:text-text-primary hover:border-border'
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {!stores.length && (
          <div className="flex items-center gap-2 mb-4">
            <Package className="w-5 h-5 text-accent-primary" />
            <h2 className="text-lg font-semibold text-text-primary">Wybierz produkt</h2>
          </div>
        )}

        {loadingProducts ? (
          <div className="flex items-center gap-3 py-4">
            <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
            <span className="text-text-secondary text-sm">Ładowanie produktów...</span>
          </div>
        ) : (
          <div className="relative">
            <select
              value={manualMode ? '__manual__' : selectedProductId}
              onChange={handleProductSelect}
              className="input w-full appearance-none pr-10"
            >
              <option value="">-- Wybierz produkt z listy --</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.productName} ({formatCurrency(p.sellingPrice)} zł)
                </option>
              ))}
              <option value="__manual__">Wpisz ręcznie...</option>
            </select>
            <ChevronDown className="w-4 h-4 text-text-secondary absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Main Grid: Input Form + Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="card animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <div className="flex items-center gap-2 mb-6">
            <DollarSign className="w-5 h-5 text-accent-primary" />
            <h2 className="text-lg font-semibold text-text-primary">Dane wejściowe</h2>
          </div>

          <div className="space-y-4">
            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Cena sprzedaży BRUTTO (zł)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.price || ''}
                onChange={(e) => updateField('price', e.target.value)}
                placeholder="0.00"
                className="input w-full"
              />
            </div>

            {/* Cost */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Koszt zakupu {isVat ? 'NETTO' : 'BRUTTO'} (zł)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.cost || ''}
                onChange={(e) => updateField('cost', e.target.value)}
                placeholder="0.00"
                className="input w-full"
              />
            </div>

            {/* Shipping */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Koszt wysyłki {isVat ? 'NETTO' : 'BRUTTO'} (zł)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.shipping || ''}
                onChange={(e) => updateField('shipping', e.target.value)}
                placeholder="0.00"
                className="input w-full"
              />
            </div>

            {/* Fee Percent */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Prowizja od transakcji (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.feePercent || ''}
                onChange={(e) => updateField('feePercent', e.target.value)}
                placeholder="0.00"
                className="input w-full"
              />
            </div>

            {/* Fee Fixed */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Stała opłata/transakcje (zł)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.feeFixed || ''}
                onChange={(e) => updateField('feeFixed', e.target.value)}
                placeholder="0.00"
                className="input w-full"
              />
            </div>

            {/* Other */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Inne koszty/szt. (zł)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.other || ''}
                onChange={(e) => updateField('other', e.target.value)}
                placeholder="0.00"
                className="input w-full"
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="card animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-accent-primary" />
            <h2 className="text-lg font-semibold text-text-primary">Wyniki</h2>
          </div>

          {!hasInput ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calculator className="w-12 h-12 text-text-disabled mb-4" />
              <p className="text-text-muted text-sm">
                Wprowadź dane po lewej stronie, aby zobaczyć wyniki kalkulacji
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* VAT */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-hover/30">
                <span className="text-sm text-text-secondary">VAT</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(results.vatAmount)} zł
                </span>
              </div>

              {/* Revenue Net */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-hover/30">
                <span className="text-sm text-text-secondary">Przychód netto</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(results.revenueNet)} zł
                </span>
              </div>

              {/* Fees */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-hover/30">
                <span className="text-sm text-text-secondary">Prowizje</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(results.fees)} zł
                </span>
              </div>

              {/* Total Costs */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-hover/30">
                <span className="text-sm text-text-secondary">Suma kosztów</span>
                <span className="text-text-primary font-medium">
                  {formatCurrency(results.totalCosts)} zł
                </span>
              </div>

              {/* Margin - highlighted */}
              <div
                className={`flex items-center justify-between p-4 rounded-xl border ${
                  results.margin >= 0
                    ? 'bg-success/10 border-success/30'
                    : 'bg-danger/10 border-danger/30'
                }`}
              >
                <span
                  className={`text-sm font-semibold ${
                    results.margin >= 0 ? 'text-success' : 'text-danger'
                  }`}
                >
                  Marża
                </span>
                <span
                  className={`text-lg font-bold ${
                    results.margin >= 0 ? 'text-success' : 'text-danger'
                  }`}
                >
                  {formatCurrency(results.margin)} zł ({results.marginPercent.toFixed(1)}%)
                </span>
              </div>

              {/* Min ROAS - highlighted */}
              <div className="flex items-center justify-between p-4 rounded-xl border bg-accent-primary/10 border-accent-primary/30">
                <span className="text-sm font-semibold text-accent-primary">Min. ROAS</span>
                <span className="text-lg font-bold text-accent-primary">
                  {results.minROAS >= 999 ? '---' : `${results.minROAS.toFixed(2)}x`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ROAS Simulation Table */}
      {hasInput && (
        <div className="card animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-accent-primary" />
            <h2 className="text-lg font-semibold text-text-primary">Symulacja ROAS</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left text-sm font-medium text-text-secondary pb-3 pr-4">
                    ROAS
                  </th>
                  <th className="text-right text-sm font-medium text-text-secondary pb-3 px-4">
                    Wydatek na reklamę
                  </th>
                  <th className="text-right text-sm font-medium text-text-secondary pb-3 px-4">
                    Zysk/szt.
                  </th>
                  <th className="text-right text-sm font-medium text-text-secondary pb-3 pl-4">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {results.roasSimulation.map((row) => (
                  <tr
                    key={row.roas}
                    className="border-b border-border-subtle last:border-b-0 hover:bg-surface-hover/30 transition-colors"
                  >
                    <td className="py-3 pr-4">
                      <span className="text-text-primary font-semibold">{row.roas.toFixed(1)}x</span>
                      {row.status === 'breakeven' && (
                        <span className="ml-2 text-xs text-warning font-medium">Break Even Point ROAS</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-text-secondary">{formatCurrency(row.adSpend)} zł</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span
                        className={`font-medium ${
                          row.profit >= 0.5
                            ? 'text-success'
                            : row.profit <= -0.5
                            ? 'text-danger'
                            : 'text-warning'
                        }`}
                      >
                        {formatCurrency(row.profit)} zł
                      </span>
                    </td>
                    <td className="py-3 pl-4 text-right">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${statusColor(row.status)} ${statusBg(row.status)}`}
                      >
                        {statusLabel(row.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
